// import { users } from '@zipdrug/react-api-sdk';
import { alertOnError } from '@zipdrug/ui';
import { mutateCreateUser, queryUsers, mutateUpdateUser } from 'graphql/users';
import { get, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { Component, createElement } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import withRoles from './withRoles';
import { buildUserQueryVariables } from './withUsers';

const withUserMutation = ComponentToWrap =>
  class UserMutationComponent extends Component {
    static propTypes = {
      mutateCreateUser: PropTypes.func,
      mutateUpdateUser: PropTypes.func,
    };
    query = {
      query: queryUsers,
      variables: { query: buildUserQueryVariables(this.props) },
    };

    handleCreateUser = variables =>
      alertOnError(
        this.props.mutateCreateUser({
          update: (cache, { data: { createUser } }) => {
            cache.modify({
              fields: {
                users: (existingUsers = {}, { readField, args, toReference }) => {
                  if (!args || !isEqual(args, this.query.variables)) {
                    return existingUsers;
                  }

                  const newUser = createUser?.data;
                  if (!newUser) {
                    return existingUsers;
                  }

                  const newUserRef = toReference(newUser);
                  const existingData = existingUsers.data || [];
                  const newUserId = readField('id', newUserRef);
                  const alreadyExists = existingData.some(
                    item => readField('id', item) === newUserId,
                  );
                  const nextData = alreadyExists
                    ? existingData
                    : [...existingData, newUserRef];
                  const nextTotal =
                    typeof existingUsers.total === 'number'
                      ? existingUsers.total + (alreadyExists ? 0 : 1)
                      : existingUsers.total;

                  return {
                    ...existingUsers,
                    data: nextData,
                    total: nextTotal,
                  };
                },
              },
            });
          },
          variables,
        }),
      );

    handleDeleteUser = variables =>
      alertOnError(
        this.props.mutateUpdateUser({
          update: cache => {
            cache.modify({
              fields: {
                users: (existingUsers = {}, { readField, args }) => {
                  if (!args || !isEqual(args, this.query.variables)) {
                    return existingUsers;
                  }

                  const userId = get(variables, 'id');
                  if (!userId) {
                    return existingUsers;
                  }

                  const existingData = existingUsers.data || [];
                  const nextData = existingData.filter(
                    item => readField('id', item) !== userId,
                  );
                  const removed = nextData.length !== existingData.length;
                  const nextTotal =
                    typeof existingUsers.total === 'number'
                      ? existingUsers.total - (removed ? 1 : 0)
                      : existingUsers.total;

                  return {
                    ...existingUsers,
                    data: nextData,
                    total: nextTotal,
                  };
                },
              },
            });
          },
          variables,
        }),
      );

    handleUpdateUser = variables => alertOnError(this.props.mutateUpdateUser({ variables }));

    render = () =>
      createElement(ComponentToWrap, {
        ...this.props,
        mutateCreateUser: this.handleCreateUser,
        mutateDeleteUser: this.handleDeleteUser,
        mutateUpdateUser: this.handleUpdateUser,
      });
  };

export default ComponentToWrap => {
  return graphql(mutateCreateUser, { name: 'mutateCreateUser' })(
    graphql(mutateUpdateUser, { name: 'mutateUpdateUser' })(
      withRoles(
        withUserMutation(ComponentToWrap)
      )
    )
  );
};
